import { useState, useEffect, useCallback } from 'react';
import type { Skill, SkillInput } from '../types.ts';

export function useSkillsManager(backendUrl: string) {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSkills = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${backendUrl}/api/skills`);
      if (!res.ok) throw new Error('Nepodařilo se načíst skilly.');
      const data = await res.json();
      setSkills(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  const toggleSkill = useCallback(async (id: string, enabled: boolean) => {
    try {
      const res = await fetch(`${backendUrl}/api/skills/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });
      if (!res.ok) throw new Error('Nepodařilo se upravit stav skillu.');
      const updated = await res.json();
      setSkills(prev => prev.map(s => (s.id === id ? updated : s)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [backendUrl]);

  const addSkill = useCallback(async (input: SkillInput) => {
    try {
      const res = await fetch(`${backendUrl}/api/skills`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input)
      });
      if (!res.ok) throw new Error('Nepodařilo se vytvořit nový skill.');
      const newSkill = await res.json();
      setSkills(prev => [...prev, newSkill]);
      return newSkill;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    }
  }, [backendUrl]);

  const updateSkillContent = useCallback(async (id: string, updates: Partial<SkillInput>) => {
    try {
      const res = await fetch(`${backendUrl}/api/skills/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (!res.ok) throw new Error('Nepodařilo se aktualizovat skill.');
      const updated = await res.json();
      setSkills(prev => prev.map(s => (s.id === id ? updated : s)));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    }
  }, [backendUrl]);

  const deleteSkill = useCallback(async (id: string) => {
    try {
      const res = await fetch(`${backendUrl}/api/skills/${id}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Nepodařilo se smazat skill.');
      setSkills(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    }
  }, [backendUrl]);

  return {
    skills,
    isLoading,
    error,
    fetchSkills,
    toggleSkill,
    addSkill,
    updateSkillContent,
    deleteSkill
  };
}
